import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["value", "placeholder"]
const _hoisted_2 = { class: "flex items-center justify-center size-10 rounded-lg bg-brown-gradient hover:bg-orange-gradient text-white" }
const _hoisted_3 = { class: "w-full border-b border-ternary flex justify-between" }
const _hoisted_4 = { class: "font-semibold text-xl leading-100p text-active" }
const _hoisted_5 = { class: "flex gap-x-4 mt-[16px] mr-[10px] mb-[16px]" }
const _hoisted_6 = {
  key: 0,
  class: "w-full h-full absolute top-0 left-0 z-[99999] overflow-auto no-scrollbar bg-white grid grid-cols-3 p-2 gap-1 overlay"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "font-semibold text-base leading-[21px] size-[42px] m-1 flex content-center justify-center flex-wrap" }
const _hoisted_9 = { class: "w-full font-medium text-lg leading-[22px]" }
const _hoisted_10 = { class: "w-full px-3 pb-5 flex justify-between gap-x-2" }

import {
  computed,
  ref,
  watch,
  nextTick,
  useTemplateRef,
  capitalize,
} from "vue";
import { useI18n } from "vue-i18n";
import ArrowIcon from "@/components/icons/ArrowIcon.vue";
import ChevronIcon from "@/components/icons/ChevronIcon.vue";
import CalendarIcon from "@/components/icons/CalendarIcon.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import { onlyDigits } from "@/utils/validators";


export default /*@__PURE__*/_defineComponent({
  __name: 'DatePicker',
  props: {
    showDatePickIcon: { type: Boolean, default: true },
    styleType: { default: "filled" },
    initialDate: {},
    minDate: {},
    maxDate: {}
  },
  emits: ["dateSelected"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;

watch(
  () => props.initialDate,
  (newDate: string | undefined) => {
    if (newDate === undefined) {
      date.value = undefined;
      return;
    }
    date.value = new Date(newDate);
  }
);

const { locale, t } = useI18n();

const dpRef = useTemplateRef<InstanceType<typeof VueDatePicker>>("datepicker");
const showYearOverlay = ref(false);
const date = ref();
const manualDate = ref<string>("");
const formattedDate = computed(() => {
  if (!date.value) return "";
  return date.value.toLocaleDateString(locale.value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
});

watch(showYearOverlay, async () => {
  if (showYearOverlay.value) {
    await nextTick();
    document
      .querySelector(".current-year-item")
      ?.scrollIntoView({ behavior: "auto", block: "center" });
  }
});

const dateSelected = (selectedDate: Date) => {
  date.value = selectedDate;
  emits("dateSelected", selectedDate);
};

function getFullMonthName(year: number, month: number) {
  const date = new Date(year, month + 1, 0);

  return capitalize(date.toLocaleDateString(locale.value, { month: "long" }));
}

function selectYear(year: number) {
  dpRef.value?.setMonthYear({ year: year });
  showYearOverlay.value = false;
}

function validateManualDate(e: KeyboardEvent) {
  if (!onlyDigits(e.key, e.ctrlKey)) {
    e.preventDefault();
  }
}

function manualDateInput(e: Event) {
  const target = e.target as HTMLInputElement;
  const digits = target.value.replace(/\D/g, "");
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  if (day.length !== 2 || month.length !== 2 || year.length !== 4) {
    updateManualDateValue(day, month, year);
    if (date.value) {
      date.value = null;
    }
  } else {
    normalizeAndSetDate(day, month, year);
  }
}

function updateManualDateValue(day: string, month: string, year: string) {
  let newManualDate = day;
  if (month.length > 0) {
    newManualDate += (day.length === 2 ? "." : "") + month;
  }
  if (year.length > 0) {
    newManualDate += (month.length === 2 ? "." : "") + year;
  }
  manualDate.value = newManualDate;
}

function normalizeAndSetDate(day: string, month: string, year: string) {
  let newDate = new Date();
  // Normalize Year
  const normalizeYear = Number(year) < 1900 ? 1900 : Number(year);
  newDate.setFullYear(normalizeYear);
  // Normalize month
  const normalizedMonth = Number(month) > 12 ? 12 : Number(month);
  newDate.setMonth(normalizedMonth - 1);
  // Normalize day
  const maxDay = new Date(
    newDate.getFullYear(),
    newDate.getMonth() + 1,
    0
  ).getDate();
  const normalizedDay = Number(day) > maxDay ? maxDay : Number(day);
  newDate.setDate(normalizedDay);

  if (props.minDate && newDate < props.minDate) {
    newDate = props.minDate;
  }
  if (props.maxDate && newDate > props.maxDate) {
    newDate = props.maxDate;
  }

  dateSelected(newDate);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["flex items-center border border-divider pl-4 pr-[6px] w-full h-[52px] rounded-[14px] font-normal text-base", {
      'bg-secondary text-muted': __props.styleType === 'filled',
      'border-muted text-black': __props.styleType === 'outline',
      'cursor-pointer': __props.showDatePickIcon,
    }])
  }, [
    _createElementVNode("input", {
      value: date.value ? formattedDate.value : manualDate.value,
      placeholder: _unref(t)('placeholders.date_placeholder'),
      class: "flex-1 bg-transparent outline-none w-full sf-pro",
      type: "text",
      maxlength: "10",
      autocomplete: "off",
      onKeydown: validateManualDate,
      onInput: manualDateInput
    }, null, 40, _hoisted_1),
    (__props.showDatePickIcon)
      ? (_openBlock(), _createBlock(_unref(VueDatePicker), {
          key: 0,
          ref: "datepicker",
          "model-value": date.value,
          "onUpdate:modelValue": dateSelected,
          "enable-time-picker": false,
          ui: { calendarCell: ['m-1'] },
          "auto-apply": "",
          locale: _unref(locale),
          class: "!w-auto",
          teleport: true,
          "min-date": props.minDate,
          "max-date": props.maxDate
        }, {
          trigger: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(CalendarIcon)
            ])
          ]),
          "month-year": _withCtx(({ month, year, handleMonthYearChange, years }) => [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", {
                class: "flex pl-[11px] pt-[17px] pb-[15px] cursor-pointer",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showYearOverlay.value = !showYearOverlay.value))
              }, [
                _createElementVNode("p", _hoisted_4, _toDisplayString(getFullMonthName(year, month)) + " " + _toDisplayString(year), 1),
                _createVNode(ChevronIcon, { class: "text-active pl-2" })
              ]),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(ArrowIcon, {
                  class: "transform-gpu -rotate-45 cursor-pointer text-brown",
                  onClick: ($event: any) => (handleMonthYearChange(false))
                }, null, 8, ["onClick"]),
                _createVNode(ArrowIcon, {
                  class: "transform-gpu rotate-[135deg] cursor-pointer text-brown",
                  onClick: ($event: any) => (handleMonthYearChange(true))
                }, null, 8, ["onClick"])
              ]),
              (showYearOverlay.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(years, (overlayYear) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: overlayYear,
                        class: _normalizeClass(["h-12 hover:cursor-pointer rounded-md text-brown flex justify-center items-center", 
                overlayYear.value === year
                  ? 'bg-active text-white current-year-item'
                  : 'hover:bg-active hover:bg-opacity-10 hover:text-active'
              ]),
                        onClick: ($event: any) => (selectYear(overlayYear.value))
                      }, _toDisplayString(overlayYear.value), 11, _hoisted_7))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          "calendar-header": _withCtx(({ day }) => [
            _createElementVNode("div", _hoisted_8, _toDisplayString(capitalize(day)), 1)
          ]),
          day: _withCtx(({ day }) => [
            _createElementVNode("div", _hoisted_9, _toDisplayString(day), 1)
          ]),
          "action-extra": _withCtx(() => [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("button", {
                class: "h-[50px] w-[167px] rounded-full border border-brown text-brown font-semibold text-[17px] leading-[22px]",
                onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (date.value = null), ["prevent"]))
              }, _toDisplayString(_unref(t)("buttons.calendar.clear")), 1),
              _createElementVNode("button", {
                class: "h-[50px] w-[167px] rounded-full border border-active text-active font-semibold text-[17px] leading-[22px]",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (dateSelected(new Date())))
              }, _toDisplayString(_unref(t)("buttons.calendar.today")), 1)
            ])
          ]),
          _: 1
        }, 8, ["model-value", "locale", "min-date", "max-date"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})